import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Logo } from '@/components/ui/Logo';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MessageList } from './MessageList';
import { InputArea } from './InputArea';
import { SetupFirstBanner } from '@/components/growth-desk/SetupFirstBanner';
import { PlanBriefBanner } from './PlanBriefBanner';
import { LoadingIndicator } from './LoadingIndicator';
import { ScrollToBottomButton } from './ScrollToBottomButton';
import { Alert } from '@/components/ui';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { apiClient, streamSSE, getAuthToken, scheduleCreditsRefresh } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useMediaWorkflow } from '@/hooks/useMediaWorkflow';
import { MediaAsset } from '@/hooks/useMediaUpload';
import { MediaGenerationCanvas } from '@/components/media/MediaGenerationCanvas';
import { MediaWorkflowStatusBar } from '@/components/media/MediaWorkflowStatusBar';
import { MediaProposalCard, MediaProposal } from './MediaProposalCard';
import { MediaIntentCard, type ImageIntent } from './MediaIntentCard';
import { CampaignStrategyConfirmationCard, CampaignStrategy, DestinationChoice } from './CampaignStrategyConfirmationCard';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { classifyPublishError } from '@/utils/publishErrors';
import { ChatSidePanel } from './ChatSidePanel';
import { useCampaignCanvas } from '@/components/canvas/useCampaignCanvas';
import { CampaignCanvas } from '@/components/canvas/CampaignCanvas';
import { SuggestionChips } from '@/components/canvas/SuggestionChips';
import { publishCampaign, type PublishAccount } from '@/components/canvas/publishService';
import { cn } from '@/lib/utils';
import type { Gate } from '@/components/canvas/types';

/**
 * Phase 4: Refactored for Instructor-based structured outputs
 *
 * BREAKING CHANGE: No longer uses marker-based extraction
 * Instead uses structured response fields from LLM:
 * - campaign_name: String (from response object)
 * - workflow_status: String (from response object)
 * - campaign_proposal: Object (from response object)
 * - response_type: String (from response object)
 *
 * This eliminates fragile regex patterns and improves reliability.
 * MEDIA tags are still embedded in text and parsed by RichMessageText.
 */

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: {
    text?: string;
    media?: Array<{
      id?: string;
      type: string;
      url: string;
      caption?: string;
      filename?: string;
      storage_key?: string;
    }>;
    campaign_proposal?: any;
    tool_results?: Array<{
      tool: string;
      status: 'success' | 'error';
      result?: any;
      error?: string;
    }>;
    // Phase 4: Structured fields from LLM (Instructor-based)
    campaign_name?: string;
    workflow_status?: string;
    response_type?: string;
    // Media generation proposals for image variations
    media_generation_proposals?: MediaProposal[];
    // Campaign strategy for user confirmation before full proposal
    campaign_strategy?: CampaignStrategy;
    // Published campaign status card (replaces plain-text publish summary)
    published_campaign_results?: { assignments: any[] };
    // Insufficient credits inline error
    insufficient_credits?: boolean;
    // Canvas envelope fields (campaign-creation redesign):
    // state_patch is replayed into the campaign canvas by the backend
    // projection; suggestions render as chips above the input.
    state_patch?: Record<string, unknown> | null;
    suggestions?: string[];
    // Canvas event payloads recorded in history (canvas_edit / approval)
    type?: string;
    canvas_edit?: { path: string; value: unknown };
    approval?: { gate: string; [key: string]: unknown };
  };
  created_at: string;
}

export interface ChatInterfaceProps {
  onConversationCreated?: () => void;
}

// Shown as the first assistant message on a new campaign. Sets expectations up
// front (per product decision): no confirmation dialog before generation, but the
// user is told images are AI-generated from their description, and that
// typography/text-led designs belong in Ad Studio.
const WELCOME_MESSAGE =
  "Hello! I'm your AI Campaign Assistant. I can help you create compelling ad " +
  "campaigns tailored to your brand. What would you like to create today?\n\n" +
  "If you don't attach images, I'll generate visuals from your description — " +
  "lifestyle and product photos, using your logo when it's set in your brand. " +
  "Prefer typography-led or text-based designs? Create those in Ad Studio and add " +
  "them to your campaign.";

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ onConversationCreated }) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentOrganization } = useAuth();
  const isMobile = useIsMobile();
  const { confirm } = useConfirm();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isDemo, setIsDemo] = useState(false);
  const [demoResetting, setDemoResetting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchedConversationMedia, setFetchedConversationMedia] = useState<Array<{
    id: string;
    filename: string;
    storage_key: string;
    url: string;
    type: string;
    source?: string; // 'upload' | 'library' | 'ai_generated'
  }>>([]);
  // Image intent confirmation state (shown when images are attached without explicit intent)
  const [mediaIntentResolve, setMediaIntentResolve] = useState<((intent: ImageIntent) => void) | null>(null);
  const [mediaIntentFilenames, setMediaIntentFilenames] = useState<string[]>([]);
  // Media generation proposals state
  const [activeProposals, setActiveProposals] = useState<MediaProposal[] | null>(null);
  const [proposalLoading, setProposalLoading] = useState(false);
  // Maximum generations allowed per session (from capabilities API)
  const [maxGenerations, setMaxGenerations] = useState<number | undefined>(undefined);
  // Campaign strategy confirmation state
  const [activeStrategy, setActiveStrategy] = useState<{ strategy: CampaignStrategy; campaignName?: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const streamingMessageRef = useRef<{ id: string; text: string; proposal: any }>({ id: '', text: '', proposal: null });

  // conversationId can be undefined for "new" campaigns
  const conversationId = campaignId !== 'new' ? campaignId : undefined;

  // Campaign canvas (split-view redesign): derived projection of this
  // conversation's campaign state, patched live from `canvas` SSE events.
  // The assistant panel beside the campaign: open while building (brief → creative), closed at
  // launch and live as on the boards; the header toggles it.
  const [chatOpen, setChatOpen] = useState(true);
  const chatDefaulted = useRef(false);
  const canvas = useCampaignCanvas(conversationId);
  useEffect(() => {
    if (chatDefaulted.current || !canvas.stage || canvas.stage === 'brief') return;
    chatDefaulted.current = true;
    if (canvas.stage === 'launch' || canvas.stage === 'live') setChatOpen(false);
  }, [canvas.stage]);
  // ?view=canvas (e.g. from plan review's Approve) opens phones on the canvas, at its Launch step.
  const [mobileTab, setMobileTab] = useState<'chat' | 'canvas'>(
    () => (new URLSearchParams(location.search).get('view') === 'canvas' ? 'canvas' : 'chat'));
  const [draft, setDraft] = useState<{ text: string; n: number }>({ text: '', n: 0 });

  // Media generation workflow hook
  const {
    isCanvasOpen: isMediaCanvasOpen,
    workflowState,
    handleClose: handleMediaCanvasClose,
    handleContinue: handleMediaContinueRaw,
    handleError: handleMediaError,
    detectMediaGeneration,
    checkWorkflowStatus,
    toggleCanvas,
    clearWorkflowState,
  } = useMediaWorkflow({
    conversationId,
    onWorkflowComplete: (selectedMedia) => {
      // Reload conversation to get the Phase 2 response
      // Add delay to allow backend to persist the message
      setTimeout(() => {
        loadConversation();
      }, 1000);
    },
    onError: (error) => {
      setError(error);
    },
  });

  // Wrapper for handleContinue that adds Phase 2 status message
  const handleMediaContinue = async (selectedMedia: any[]) => {
    // Clear workflow state immediately to hide status bar and prevent re-triggering
    clearWorkflowState();

    // Add status message indicating Phase 2 is starting
    const statusMessageId = `phase2-status-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      {
        id: statusMessageId,
        role: 'assistant' as const,
        content: {
          text: '___LOADING_INDICATOR___Generating your campaign with selected media...'
        },
        created_at: new Date().toISOString()
      }
    ]);

    try {
      // Call the original continue function
      await handleMediaContinueRaw(selectedMedia);

      // Remove status message after completion (it will be replaced by actual response)
      setMessages(prev => prev.filter(msg => msg.id !== statusMessageId));
    } catch (error) {
      // Remove status message on error
      setMessages(prev => prev.filter(msg => msg.id !== statusMessageId));
      throw error;
    }
  };

  // Handle media generation proposal approval
  const handleProposalApprove = async (
    approvedIds: string[],
    modifications: Record<string, string>
  ) => {
    if (!conversationId) return;

    setProposalLoading(true);
    try {
      const response = await apiClient.post(
        `/api/v1/campaigns/${conversationId}/approve-generation`,
        {
          approved_proposals: approvedIds,
          rejected_proposals: activeProposals
            ?.filter(p => !approvedIds.includes(p.proposal_id))
            .map(p => p.proposal_id) || [],
          skip_all: false,
          user_modifications: modifications
        }
      );

      if (response.data.success) {
        // Clear proposals and show confirmation
        setActiveProposals(null);

        // Add a message indicating generation started
        const statusMessage: Message = {
          id: `gen-status-${Date.now()}`,
          role: 'assistant',
          content: {
            text: `Starting image generation for ${response.data.jobs_created?.length || approvedIds.length} variation(s). This may take a few minutes...`,
          },
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, statusMessage]);

        // Optionally, poll for job completion or handle via workflow
        if (response.data.jobs_created?.length > 0) {
          // Open media canvas to show generation progress
          checkWorkflowStatus(true);
        }
      }
    } catch (err: any) {
      console.error('Failed to approve proposals:', err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.response?.data?.error?.message;

      if (status === 403) {
        setError('Image and video generation is not available on your current plan. Please upgrade to unlock this feature.');
      } else {
        setError(detail || err.message || 'Failed to start media generation');
      }
    } finally {
      setProposalLoading(false);
    }
  };

  // Handle skipping media generation proposals
  const handleProposalSkip = async () => {
    if (!conversationId) {
      setActiveProposals(null);
      return;
    }

    setProposalLoading(true);
    try {
      await apiClient.post(
        `/api/v1/campaigns/${conversationId}/approve-generation`,
        {
          approved_proposals: [],
          rejected_proposals: [],
          skip_all: true
        }
      );

      // Clear proposals
      setActiveProposals(null);

      // Send a follow-up message to continue with campaign creation
      // This triggers the LLM to proceed using the uploaded images
      // Show friendly text in chat, send detailed instruction to LLM
      await handleSendMessage(
        'Continue with the campaign creation using my uploaded images. Skip AI image generation.',
        undefined,  // no new files
        undefined,  // no selected tools
        undefined,  // no library assets
        'Skip image generation, use my uploaded images.'
      );

    } catch (err: any) {
      console.error('Failed to skip proposals:', err);
      // Even on error, clear the proposals UI
      setActiveProposals(null);
    } finally {
      setProposalLoading(false);
    }
  };

  // Handle campaign strategy approval
  const handleStrategyApprove = async (destinationChoice?: DestinationChoice, confirmedUrl?: string) => {
    setActiveStrategy(null);
    const destinationInstruction =
      destinationChoice === 'website' ? ' Use website campaigns only — do not include WhatsApp or lead gen ads.' :
      destinationChoice === 'phone' ? ' Use WhatsApp/lead gen campaigns only — do not include website link_url campaigns.' :
      destinationChoice === 'both' ? ' Include both website traffic campaigns and WhatsApp/lead gen ads.' :
      '';
    const urlInstruction = confirmedUrl
      ? ` Use exactly "${confirmedUrl}" as the link_url and landing_page_url for all ads — do not modify or append to this URL.`
      : '';
    setMessages(prev => [
      ...prev,
      {
        id: `stage-progress-${Date.now()}`,
        role: 'system' as const,
        content: { text: 'Generating creative concepts...', type: 'stage_progress' },
        created_at: new Date().toISOString(),
      },
    ]);
    await handleSendMessage(
      `Strategy approved.${destinationInstruction}${urlInstruction}`,
      undefined, undefined, undefined, undefined, true
    );
  };

  // Handle campaign strategy change requests
  const handleStrategyRequestChanges = async (feedback: string) => {
    setActiveStrategy(null);
    // Send change request to LLM which triggers revised strategy (Flow E again)
    await handleSendMessage(
      `I'd like to adjust the campaign strategy: ${feedback}`,
      undefined,
      undefined,
      undefined
    );
  };

  useEffect(() => {
    if (conversationId) {
      loadConversation();
    } else {
      // No conversation ID - show welcome message
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: {
          text: WELCOME_MESSAGE,
        },
        created_at: new Date().toISOString(),
      }]);
    }
  }, [conversationId]);

  // Listen for post-publish status events dispatched by CampaignProposalCard
  // and inject an interactive PublishedCampaignStatus card into the chat.
  useEffect(() => {
    const handleCampaignPublished = async (e: Event) => {
      const { conversationId: eventConvId, results } = (e as CustomEvent).detail ?? {};
      if (eventConvId !== conversationId || !Array.isArray(results)) return;

      const successCount = results.filter((r: any) => r.status === 'success').length;
      const failCount = results.filter((r: any) => r.status === 'failed').length;
      const newStatus = failCount === 0 ? 'published' : successCount > 0 ? 'partially_published' : 'publish_failed';

      // Optimistically show a plain-text fallback immediately while we fetch assignments
      const tempId = `publish-status-${Date.now()}`;
      setMessages((prev) => [...prev, {
        id: tempId,
        role: 'assistant' as const,
        content: { text: '**Campaign publishing complete.** Fetching status…' },
        created_at: new Date().toISOString(),
      }]);

      try {
        // Fetch the fresh platform assignments (includes platform_campaign_status from DB)
        // Retry once after 2s to handle the case where publisher-service hasn't committed yet
        let assignments: any[] = [];
        for (let attempt = 0; attempt < 2; attempt++) {
          if (attempt > 0) await new Promise((r) => setTimeout(r, 2000));
          const assignmentsResp = await apiClient.get(
            `/api/v1/conversations/${conversationId}/platform-assignments`
          );
          assignments = assignmentsResp.data?.assignments ?? [];
          if (assignments.length > 0) break;
        }

        // If DB still empty, synthesize from the publish results so the card always shows rows
        if (assignments.length === 0 && results.length > 0) {
          assignments = results.map((r: any) => ({
            id: r.connectionId,
            platform: r.platform,
            platform_account_id: '',
            account_name: r.accountName,
            platform_campaign_id: r.platformCampaignId ?? null,
            status: r.status === 'success' ? 'published' : 'failed',
            platform_campaign_status: r.finalStatus ?? null,
            platform_campaign_status_synced_at: null,
            published_at: new Date().toISOString(),
          }));
        }

        // Build the structured message content
        const structuredContent = { published_campaign_results: { assignments } };

        // Replace the optimistic placeholder with the real structured card
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId
              ? { ...m, content: structuredContent }
              : m
          )
        );

        // Persist structured message + update conversation status
        await Promise.all([
          apiClient.post(`/api/v1/conversations/${conversationId}/messages`, {
            role: 'assistant',
            content: structuredContent,
          }),
          apiClient.patch(`/api/v1/conversations/${conversationId}/status`, {
            status: newStatus,
          }),
        ]);

        // Refresh sidebar to show updated status icon
        if (onConversationCreated) onConversationCreated();
      } catch (err) {
        // Fallback: replace placeholder with plain-text summary
        console.warn('[ChatInterface] Failed to load assignments after publish:', err);
        const lines: string[] = [];
        results.forEach((r: any) => {
          const icon = r.status === 'success' ? '✅' : '❌';
          lines.push(`${icon} **${r.accountName}** (${r.platform})`);
          if (r.error) {
            const errInfo = classifyPublishError(r.error);
            lines.push(`   ↳ ${errInfo.title} — ${errInfo.hint}`);
          }
        });
        if (failCount === 0) lines.push(`\nAll ${successCount} published. Activate them in the platform dashboard.`);
        else if (successCount > 0) lines.push(`\n${successCount} published, ${failCount} failed.`);
        else lines.push('\nAll accounts failed to publish.');

        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId
              ? { ...m, content: { text: lines.join('\n') } }
              : m
          )
        );
      }
    };

    window.addEventListener('campaignPublished', handleCampaignPublished);
    return () => window.removeEventListener('campaignPublished', handleCampaignPublished);
  }, [conversationId, onConversationCreated]);

  // Fetch media generation capabilities when proposals are shown
  useEffect(() => {
    if (activeProposals && activeProposals.length > 0) {
      const fetchCapabilities = async () => {
        try {
          const response = await apiClient.get('/api/v1/media/capabilities');
          const regenLimit = response.data.regeneration_attempts_per_campaign;
          // -1 means unlimited
          if (regenLimit && regenLimit !== -1) {
            setMaxGenerations(regenLimit);
          } else {
            setMaxGenerations(undefined);
          }
        } catch (err) {
          console.error('Failed to fetch media capabilities:', err);
          // Don't block on error, just proceed without limit
        }
      };
      fetchCapabilities();
    }
  }, [activeProposals]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleDemoReset = async () => {
    if (!conversationId) return;
    setDemoResetting(true);
    try {
      await apiClient.post(`/api/v1/conversations/${conversationId}/reset-demo`);
      setActiveStrategy(null);
      setActiveProposals(null);
      await loadConversation();
    } catch (err: any) {
      console.error('Failed to reset demo:', err);
    } finally {
      setDemoResetting(false);
    }
  };

  const loadConversation = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/api/v1/campaigns/${conversationId}`);
      const data = response.data;
      setIsDemo(data.conversation?.is_demo ?? false);
      if (data.messages && Array.isArray(data.messages)) {
        // Phase 4: No marker stripping needed - use structured fields
        // Messages already have clean text from backend (no markers)
        // MEDIA tags are still embedded in text for RichMessageText to parse
        const filteredMessages = data.messages
          .filter((msg: Message) => {
            // Drop canvas housekeeping records — they're not chat turns
            const t = msg.content?.type;
            return t !== 'canvas_edit' && t !== 'approval' && t !== 'stage_progress';
          })
          .map((msg: Message) => {
            if (msg.role === 'user' && msg.content.text) {
              // Remove media context tags from user messages only
              const cleanText = msg.content.text
                .replace(/\n\n\[User (uploaded|REPLACED previous|ADDED new) media:.*?\]/g, '')
                .replace(/\[User (uploaded|REPLACED previous|ADDED new) media:.*?\]/g, '')
                .replace(/\n\[User wants to use their uploaded.*?\]/g, '')
                .replace(/\n\[User wants AI variations.*?\]/g, '')
                .replace(/\n\[User wants completely new AI.*?\]/g, '')
                .trim();
              return { ...msg, content: { ...msg.content, text: cleanText } };
            }
            return msg;
          });

        // Check if any message already has campaign_proposal in content (single-pass and two-pass workflows)
        const hasProposalInMessages = filteredMessages.some(
          (m: Message) => m.role === 'assistant' && m.content?.campaign_proposal
        );

        // Only use conversation-level campaign_proposal if not found in messages
        if (!hasProposalInMessages && data.campaign_proposal) {
          // Find the last assistant message that is a campaign creation/modification response
          // Don't attach stale campaign_proposal to strategy confirmation or other non-campaign messages
          const lastCampaignAssistantIndex = filteredMessages
            .map((m: Message, idx: number) => ({ m, idx }))
            .filter(({ m }) => m.role === 'assistant' && m.content?.response_type !== 'campaign_strategy_confirmation')
            .pop()?.idx;

          const targetIndex = lastCampaignAssistantIndex ?? -1;
          if (targetIndex !== -1) {
            filteredMessages[targetIndex] = {
              ...filteredMessages[targetIndex],
              content: {
                ...filteredMessages[targetIndex].content,
                campaign_proposal: data.campaign_proposal
              }
            };
          }
        }

        setMessages(filteredMessages);

        // Restore suggestion chips from the last assistant envelope, and
        // sync the canvas projection for this conversation.
        const lastWithSuggestions = [...filteredMessages]
          .reverse()
          .find((m: Message) => m.role === 'assistant' && (m.content?.suggestions?.length ?? 0) > 0);
        canvas.setSuggestions(lastWithSuggestions?.content.suggestions ?? []);
        canvas.refresh();

        // ── Scheduler publish recovery ──────────────────────────────────────
        // Campaigns published by the background scheduler (or a mix of immediate +
        // scheduled) may have platform assignments in the DB that were never surfaced
        // in a status card. Only run for conversations that went through the launch gate.
        const hasLaunchApproval = data.messages.some(
          (m: any) => m.role === 'user' && m.content?.approval?.gate === 'launch',
        );
        if (hasLaunchApproval) {
          try {
            const coveredIds = new Set(
              filteredMessages
                .filter((m: Message) =>
                  m.role === 'assistant' &&
                  Array.isArray((m.content as any)?.published_campaign_results?.assignments),
                )
                .flatMap((m: Message) =>
                  ((m.content as any).published_campaign_results.assignments as any[]).map(
                    (a: any) => a.id,
                  ),
                ),
            );

            const assignmentsResp = await apiClient.get(
              `/api/v1/conversations/${conversationId}/platform-assignments`,
            );
            const allPublished = (assignmentsResp.data?.assignments ?? []).filter(
              (a: any) =>
                (a.platform_campaign_id && (!a.publish_type || a.publish_type === 'paid_ad')) ||
                (a.publish_type === 'organic_post' && a.status === 'published'),
            );
            // Only inject for assignments not already in any existing status card.
            // Using uncovered (not allPublished) avoids repeating rows already shown.
            const uncovered = allPublished.filter((a: any) => !coveredIds.has(a.id));

            if (uncovered.length > 0) {
              const structuredContent = { published_campaign_results: { assignments: uncovered } };
              const statusMsg = {
                id: `scheduler-publish-status-${Date.now()}`,
                role: 'assistant' as const,
                content: structuredContent,
                created_at: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, statusMsg]);
              await apiClient.post(`/api/v1/conversations/${conversationId}/messages`, {
                role: 'assistant',
                content: structuredContent,
              });
            }
          } catch {
            // Non-critical — status card will appear on next publish action
          }
        }
      }

      // Fetch conversation media (Phase 3.5: Media rendering on reload)
      try {
        const mediaResponse = await apiClient.get(`/api/v1/media/campaigns/${conversationId}`);
        if (mediaResponse.data.media && Array.isArray(mediaResponse.data.media)) {
          const mediaList = mediaResponse.data.media.map((m: any) => ({
            id: m.id,
            filename: m.filename,          // Original filename for LLM and display
            storage_key: m.storage_key,    // UUID for storage reference
            url: m.public_url,
            type: m.media_type,
            source: m.source,             // 'upload' | 'library' | 'ai_generated'
          }));
          setFetchedConversationMedia(mediaList);
        }
      } catch (mediaErr) {
        console.error('Failed to load conversation media:', mediaErr);
        // Non-fatal error - conversation loads even if media fetch fails
      }

      // Check for pending media generation proposals
      if (data.campaign_config?.media_generation_proposals) {
        const proposals = data.campaign_config.media_generation_proposals;
        if (Array.isArray(proposals) && proposals.length > 0) {
          console.log('[ChatInterface] Found pending proposals in conversation:', proposals.length);
          setActiveProposals(proposals);
        }
      }

      // Check for pending campaign strategy confirmation.
      // Search backward for the last strategy message that has NOT been followed
      // by a user message (i.e. the user hasn't responded to it yet).
      if (data.messages && Array.isArray(data.messages)) {
        const msgs = data.messages;
        let pendingStrategy: { strategy: CampaignStrategy; campaignName?: string } | null = null;
        for (let i = msgs.length - 1; i >= 0; i--) {
          const msg = msgs[i];
          if (msg.role === 'user') {
            // A user message after the strategy means it was already acted on
            break;
          }
          if (
            msg.role === 'assistant' &&
            msg.content?.campaign_strategy &&
            msg.content?.response_type === 'campaign_strategy_confirmation'
          ) {
            pendingStrategy = {
              strategy: msg.content.campaign_strategy,
              campaignName: msg.content.campaign_name,
            };
            break;
          }
        }
        if (pendingStrategy) {
          console.log('[ChatInterface] Found pending strategy confirmation in conversation');
          setActiveStrategy(pendingStrategy);
        }
      }

      // Resume nudge: if the last message is from the user with no assistant reply,
      // the conversation is mid-flight. Nothing to show — the user already knows.
      // (We don't add a nudge here intentionally; the empty input area is sufficient.)

      // If no messages yet (newly created conversation), show welcome message
      if (!data.messages || data.messages.length === 0) {
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          content: {
            text: WELCOME_MESSAGE,
          },
          created_at: new Date().toISOString(),
        }]);
      }

      // Fix #2: Check for active media workflow after conversation loads
      // This handles the reload scenario where canvas should auto-open
      if (conversationId) {
        checkWorkflowStatus();
      }
    } catch (err: any) {
      console.error('Failed to load conversation:', err);

      // If conversation not found (404) or access denied (403), clear selection and go to campaigns
      if (err.response?.status === 404 || err.response?.status === 403) {
        // Dispatch event to clear the selected campaign
        window.dispatchEvent(new CustomEvent('conversationNotFound', {
          detail: { conversationId }
        }));
      } else {
        setError(err.message || 'Failed to load conversation');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStreamingMessage = async (
    conversationId: string | undefined,
    text: string,
    allConversationMedia: Array<{ id: string; type: string; url: string; filename: string; storage_key: string }>,
    mediaContext: string,
    selectedTools: string[] | undefined,
    tempId: string,
    imageIntent?: ImageIntent
  ) => {
    /**
     * Handle streaming message using Server-Sent Events
     */
    let accumulatedText = '';
    let campaignProposal: any = null;
    const token = getAuthToken();

    // Waiting state: a status bubble shown from send until the reply arrives, retitled with
    // the model's streamed thought summaries (`thinking` events) while it reasons.
    const thinkingMessageId = `thinking-${Date.now()}`;
    let thinkingShown = true;
    setMessages(prev => [...prev, {
      id: thinkingMessageId,
      role: 'assistant',
      content: { text: '___LOADING_INDICATOR___Thinking…' },
      created_at: new Date().toISOString(),
    }]);
    const clearThinking = () => {
      if (!thinkingShown) return;
      thinkingShown = false;
      setMessages(prev => prev.filter(msg => msg.id !== thinkingMessageId));
    };

    // Helper to yield control to browser for rendering
    const yieldToRenderer = () => new Promise(resolve => setTimeout(resolve, 0));

    try {
      const streamUrl = `/api/v1/campaigns/${conversationId || 'new'}/prompt-stream`;
      const apiBaseURL = import.meta.env.VITE_API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:8080`;
      const fullUrl = streamUrl.startsWith('http') ? streamUrl : `${apiBaseURL}${streamUrl}`;

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          message: text + mediaContext,
          media: allConversationMedia.map(m => ({
            id: m.id,
            type: m.type,
            filename: m.filename,
            storage_key: m.storage_key,
            url: m.url
          })),
          ...(imageIntent && { image_intent: imageIntent }),
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error('Streaming connection failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      const messageId = `msg-${Date.now()}`;
      let assistantMessageAdded = false;
      let lastUpdateTime = Date.now();
      let streamingComplete = false;
      let proposalStatusMessageId = `status-${Date.now()}`;
      let proposalStatusShown = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];

          if (line.startsWith('event: ')) {
            const eventType = line.slice(7).trim();
            const dataLine = i + 1 < lines.length ? lines[i + 1] : '';

            if (eventType === 'thinking' && dataLine.startsWith('data: ')) {
              try {
                const data = JSON.parse(dataLine.slice(6));
                const title = (data.title || '').trim();
                if (thinkingShown && title) {
                  setMessages(prev =>
                    prev.map(msg =>
                      msg.id === thinkingMessageId
                        ? { ...msg, content: { ...msg.content, text: `___LOADING_INDICATOR___${title}…` } }
                        : msg
                    )
                  );
                }
                i++; // Skip the data line
              } catch (e) {
                console.error('Failed to parse thinking event', e);
              }
            } else if (eventType === 'message' && dataLine.startsWith('data: ')) {
              clearThinking();
              try {
                const data = JSON.parse(dataLine.slice(6));
                accumulatedText += data.text || '';

                // Phase 4: No marker stripping needed
                // Text comes clean from backend, just use it directly
                const displayText = accumulatedText.trim();

                // Update ref with new text (no setState - no re-render yet)
                streamingMessageRef.current.id = messageId;
                streamingMessageRef.current.text = displayText;

                // Only update UI every 100ms to avoid blocking
                const now = Date.now();
                if (now - lastUpdateTime > 100) {
                  lastUpdateTime = now;

                  if (!assistantMessageAdded) {
                    const newMessage: Message = {
                      id: messageId,
                      role: 'assistant',
                      content: {
                        text: displayText,
                        campaign_proposal: campaignProposal,
                      },
                      created_at: new Date().toISOString(),
                    };
                    setMessages(prev => [...prev, newMessage]);
                    assistantMessageAdded = true;
                  } else {
                    // Only update the specific message
                    setMessages(prev =>
                      prev.map(msg =>
                        msg.id === messageId
                          ? { ...msg, content: { ...msg.content, text: displayText } }
                          : msg
                      )
                    );
                  }

                  await yieldToRenderer();
                }
                i++; // Skip the data line
              } catch (e) {
                console.error('Failed to parse message event', e);
              }
            } else if (eventType === 'proposal' && dataLine.startsWith('data: ')) {
              try {
                const data = JSON.parse(dataLine.slice(6));
                campaignProposal = data.campaign_proposal;
                console.log('[ChatInterface] Received proposal event:', {
                  hasCampaignProposal: !!campaignProposal,
                  proposalKeys: campaignProposal ? Object.keys(campaignProposal) : [],
                  hasDisplay: !!(campaignProposal?.display),
                  hasCanonical: !!(campaignProposal?.canonical),
                  displayCampaigns: campaignProposal?.display?.campaigns?.length || 0
                });
                // Phase 4: Extract structured fields from response
                const responseData = data.response || {};
                const campaignName = responseData.campaign_name;
                const workflowStatus = responseData.workflow_status;
                const responseType = responseData.response_type;

                // Check for media generation proposals
                if (data.media_generation_proposals && data.media_generation_proposals.length > 0) {
                  console.log('[ChatInterface] Received media generation proposals:', data.media_generation_proposals.length);
                  setActiveProposals(data.media_generation_proposals);
                }

                // Remove the status message and update main message with proposal
                flushSync(() =>
                  setMessages(prev => {
                    const filtered = prev.filter(msg => msg.id !== proposalStatusMessageId);

                    // Check if assistant message exists
                    const messageExists = filtered.some(msg => msg.id === messageId);

                    if (!messageExists) {
                      // Message not added yet (proposal arrived before 100ms throttle)
                      // Add it now with the proposal
                      console.log('[ChatInterface] Adding assistant message with proposal (message not found)');
                      return [...filtered, {
                        id: messageId,
                        role: 'assistant' as const,
                        content: {
                          text: streamingMessageRef.current.text || '',
                          campaign_proposal: campaignProposal,
                          campaign_name: campaignName,
                          workflow_status: workflowStatus,
                          response_type: responseType
                        },
                        created_at: new Date().toISOString()
                      }];
                    }

                    // Message exists, update it with proposal
                    return filtered.map(msg => {
                      if (msg.id === messageId) {
                        return {
                          ...msg,
                          content: {
                            ...msg.content,
                            campaign_proposal: campaignProposal,
                            campaign_name: campaignName,
                            workflow_status: workflowStatus,
                            response_type: responseType
                          }
                        };
                      }
                      return msg;
                    });
                  })
                );
                assistantMessageAdded = true; // Mark as added since we just ensured it exists
                i++; // Skip the data line
              } catch (e) {
                console.error('Failed to parse proposal event', e);
              }
            } else if (eventType === 'media-proposals' && dataLine.startsWith('data: ')) {
              // Handle media generation proposals (image variation confirmation flow)
              try {
                const data = JSON.parse(dataLine.slice(6));
                console.log('[ChatInterface] Received media-proposals event:', {
                  proposalCount: data.proposals?.length,
                  responseType: data.response_type,
                  hasMessage: !!data.message
                });

                if (data.proposals && data.proposals.length > 0) {
                  setActiveProposals(data.proposals);
                }

                // Update the assistant message with the proposals
                if (assistantMessageAdded) {
                  flushSync(() =>
                    setMessages(prev =>
                      prev.map(msg =>
                        msg.id === messageId
                          ? {
                              ...msg,
                              content: {
                                ...msg.content,
                                media_generation_proposals: data.proposals,
                                response_type: data.response_type
                              }
                            }
                          : msg
                      )
                    )
                  );
                }

                // Remove status message if shown
                if (proposalStatusShown) {
                  setMessages(prev => prev.filter(msg => msg.id !== proposalStatusMessageId));
                }

                i++; // Skip the data line
              } catch (e) {
                console.error('Failed to parse media-proposals event', e);
              }
            } else if (eventType === 'campaign-strategy' && dataLine.startsWith('data: ')) {
              // Handle campaign strategy confirmation (strategy review before full proposal)
              try {
                const data = JSON.parse(dataLine.slice(6));
                console.log('[ChatInterface] Received campaign-strategy event:', {
                  hasStrategy: !!data.campaign_strategy,
                  responseType: data.response_type,
                  campaignName: data.campaign_name
                });

                if (data.campaign_strategy) {
                  setActiveStrategy({
                    strategy: data.campaign_strategy,
                    campaignName: data.campaign_name
                  });
                }

                // Update the assistant message with the strategy
                if (assistantMessageAdded) {
                  flushSync(() =>
                    setMessages(prev =>
                      prev.map(msg =>
                        msg.id === messageId
                          ? {
                              ...msg,
                              content: {
                                ...msg.content,
                                campaign_strategy: data.campaign_strategy,
                                response_type: data.response_type
                              }
                            }
                          : msg
                      )
                    )
                  );
                }

                // Remove status message if shown
                if (proposalStatusShown) {
                  setMessages(prev => prev.filter(msg => msg.id !== proposalStatusMessageId));
                }

                i++; // Skip the data line
              } catch (e) {
                console.error('Failed to parse campaign-strategy event', e);
              }
            } else if (eventType === 'message-end') {
              console.log('Text streaming completed, waiting for proposal...');
              console.log(`proposalStatusShown: ${proposalStatusShown}, assistantMessageAdded: ${assistantMessageAdded}`);
              // Show status message that proposal is being prepared
              if (!proposalStatusShown && assistantMessageAdded) {
                console.log('Adding status message...');
                const statusMessage: Message = {
                  id: proposalStatusMessageId,
                  role: 'assistant',
                  content: {
                    text: '___LOADING_INDICATOR___Generating campaign proposal...',
                  },
                  created_at: new Date().toISOString(),
                };
                console.log('Status message created:', statusMessage);
                flushSync(() => {
                  setMessages(prev => {
                    console.log('Setting messages with status...');
                    return [...prev, statusMessage];
                  });
                });
                proposalStatusShown = true;
                console.log('Status message added, proposalStatusShown set to true');
              } else {
                console.log('Status message NOT added - proposalStatusShown:', proposalStatusShown, 'assistantMessageAdded:', assistantMessageAdded);
              }
            } else if (eventType === 'canvas' && dataLine.startsWith('data: ')) {
              try {
                const data = JSON.parse(dataLine.slice(6));
                // Canvas envelope: apply the patch optimistically (with flash)
                // and surface the suggestion chips. The server projection is
                // re-fetched on stream end to reconcile.
                if (data.state_patch) {
                  canvas.applyPatch(data.state_patch);
                }
                canvas.setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
                if (isMobile) setMobileTab('canvas');
                i++; // Skip the data line
              } catch (e) {
                console.error('Failed to parse canvas event', e);
              }
            } else if (eventType === 'end' && dataLine.startsWith('data: ')) {
              clearThinking();
              try {
                const data = JSON.parse(dataLine.slice(6));
                console.log('Streaming completed with data:', data);
                streamingComplete = true;
                // Reconcile the canvas with the authoritative server projection
                canvas.refresh();
                // This turn's charge (and any image holds it started) is now on the balance.
                scheduleCreditsRefresh();

                // If workflow_status is present, update the assistant message
                if (data.workflow_status && assistantMessageAdded) {
                  setMessages(prev =>
                    prev.map(msg =>
                      msg.id === messageId
                        ? {
                            ...msg,
                            content: {
                              ...msg.content,
                              workflow_status: data.workflow_status
                            }
                          }
                        : msg
                    )
                  );
                }

                // Clean up status message if proposal didn't arrive
                if (proposalStatusShown) {
                  setMessages(prev => prev.filter(msg => msg.id !== proposalStatusMessageId));
                }
                i++; // Skip the data line
              } catch (e) {
                console.error('Failed to parse end event', e);
                streamingComplete = true;
                // Clean up status message on error
                if (proposalStatusShown) {
                  setMessages(prev => prev.filter(msg => msg.id !== proposalStatusMessageId));
                }
              }
            } else if (eventType === 'error' && dataLine.startsWith('data: ')) {
              clearThinking();
              try {
                const data = JSON.parse(dataLine.slice(6));
                console.error('Streaming error:', data.error);
                console.error('Full error data:', data);
                // Clean up status message on error
                if (proposalStatusShown) {
                  setMessages(prev => prev.filter(msg => msg.id !== proposalStatusMessageId));
                }
                scheduleCreditsRefresh();
                if (data.code === 'insufficient_credits' || data.code === 'turn_limit') {
                  const creditsErrorMessage: Message = {
                    id: `error-${Date.now()}`,
                    role: 'system',
                    content: {
                      insufficient_credits: data.code === 'insufficient_credits',
                      text: data.error,
                    },
                    created_at: new Date().toISOString(),
                  };
                  setMessages(prev => [...prev, creditsErrorMessage]);
                } else {
                  setError(data.error || 'Streaming error occurred');
                }
              } catch (e) {
                console.error('Failed to parse error event', e);
              }
            } else if (eventType === 'media-workflow' && dataLine.startsWith('data: ')) {
              try {
                const data = JSON.parse(dataLine.slice(6));
                console.log('Media workflow event received:', data);
                // Trigger canvas to open for new workflow (forceOpen=true)
                checkWorkflowStatus(true);
                i++; // Skip the data line
              } catch (e) {
                console.error('Failed to parse media-workflow event', e);
              }
            }
          }
        }
      }

      clearThinking();

      // Final render with complete accumulated text
      if (assistantMessageAdded && streamingMessageRef.current.text) {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? { ...msg, content: { ...msg.content, text: streamingMessageRef.current.text, campaign_proposal: campaignProposal } }
              : msg
          )
        );
      }

      // Phase 4: Extract campaign name from structured response field (not regex)
      // The campaign_name will be populated by the proposal event handler above
      // It will be available in the message content when proposal arrives
      // Campaign name update to API happens in non-streaming path (see below)
      // For streaming, we wait for proposal event which has the structured data

      // Refresh sidebar title if campaign name was updated
      if (onConversationCreated) {
        onConversationCreated();
      }
    } catch (err: any) {
      console.error('Streaming error:', err);
      clearThinking();
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      if (err.message && err.message.toLowerCase().includes('insufficient credits')) {
        const creditsErrorMessage: Message = {
          id: `error-${Date.now()}`,
          role: 'system',
          content: {
            insufficient_credits: true,
            text: "You've run out of credits. Upgrade your plan or purchase more credits to continue generating campaigns.",
          },
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, creditsErrorMessage]);
      } else {
        setError(err.message || 'Streaming failed');
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          role: 'system',
          content: {
            text: `Error: ${err.message || 'Streaming failed. Please try again.'}`,
          },
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    }
  };

  const handleSendMessage = async (
    text: string,
    files?: File[],
    selectedTools?: string[],
    libraryAssets?: MediaAsset[],
    displayText?: string,
    suppressUserBubble?: boolean
  ) => {
    if (!text.trim() && !files?.length && !libraryAssets?.length) return;
    const tempId = `temp-${Date.now()}`;
    if (!suppressUserBubble) {
      const userMessage: Message = {
        id: tempId,
        role: 'user',
        content: { text: displayText || text },
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, userMessage]);
    }
    setLoading(true);
    setError(null);

    try {
      // Upload files if any (Phase 2.3: With duplicate handling)
      let mediaUrls: Array<{ id: string; type: string; url: string; filename: string; storage_key: string }> = [];
      if (files && files.length > 0) {
        // Upload each file individually to media-generation-service
        const uploadPromises = files.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);

          try {
            const uploadResponse = await apiClient.post('/api/v1/media/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            return {
              id: uploadResponse.data.asset.id,
              type: uploadResponse.data.asset.media_type || (file.type.startsWith('image/') ? 'image' : 'video'),
              url: uploadResponse.data.asset.public_url,
              filename: uploadResponse.data.asset.filename,        // Now original name from backend
              storage_key: uploadResponse.data.asset.storage_key  // UUID for storage
            };
          } catch (error: any) {
            // Phase 2.3: Handle duplicate file detection
            if (error.response?.status === 409) {
              const duplicateInfo = error.response.data;
              const uploadedOn = new Date(duplicateInfo.existing_asset.created_at).toLocaleDateString();
              const shouldReplace = await confirm(
                `"${file.name}" was already uploaded on ${uploadedOn}. Replace it or keep the existing copy?`,
                {
                  title: 'File already exists',
                  variant: 'info',
                  confirmLabel: 'Replace',
                  cancelLabel: 'Keep existing',
                }
              );

              if (shouldReplace) {
                // Re-upload with replace flag
                const replaceResponse = await apiClient.post(
                  '/api/v1/media/upload?replace_existing=true',
                  formData,
                  { headers: { 'Content-Type': 'multipart/form-data' } }
                );
                return {
                  id: replaceResponse.data.asset.id,
                  type: replaceResponse.data.asset.media_type || (file.type.startsWith('image/') ? 'image' : 'video'),
                  url: replaceResponse.data.asset.public_url,
                  filename: replaceResponse.data.asset.filename,
                  storage_key: replaceResponse.data.asset.storage_key
                };
              } else {
                // Use existing file
                return {
                  id: duplicateInfo.existing_asset.id,
                  type: duplicateInfo.existing_asset.media_type,
                  url: duplicateInfo.existing_asset.public_url,
                  filename: duplicateInfo.existing_asset.filename,
                  storage_key: duplicateInfo.existing_asset.storage_key
                };
              }
            }
            throw error;
          }
        });

        mediaUrls = await Promise.all(uploadPromises);
      }

      // Phase 3.4: Handle library asset selections (link without duplication)
      let libraryMediaUrls: Array<{ id: string; type: string; url: string; filename: string; storage_key: string }> = [];
      if (libraryAssets && libraryAssets.length > 0 && conversationId) {
        // Link each library asset to the conversation
        const linkPromises = libraryAssets.map(async (asset) => {
          try {
            await apiClient.post(
              `/api/v1/media/campaigns/${conversationId}/${asset.id}?source=library`
            );
            return {
              id: asset.id,
              type: asset.media_type,
              url: asset.public_url,
              filename: asset.filename,
              storage_key: asset.storage_key || asset.id
            };
          } catch (error) {
            console.error(`Failed to link asset ${asset.id}:`, error);
            // Still return the asset info even if link fails (might already be linked)
            return {
              id: asset.id,
              type: asset.media_type,
              url: asset.public_url,
              filename: asset.filename,
              storage_key: asset.storage_key || asset.id
            };
          }
        });

        libraryMediaUrls = await Promise.all(linkPromises);
      }

      // Combine uploaded and library media
      const allNewMedia = [...mediaUrls, ...libraryMediaUrls];

      // Phase 3.4: Update message with all media (uploads + library selections) in ONE update
      if (allNewMedia.length > 0) {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === tempId
              ? { ...msg, content: { ...msg.content, media: allNewMedia.map(m => ({
                  id: m.id,
                  type: m.type,
                  url: m.url,
                  filename: m.filename,
                  storage_key: m.storage_key
                })) } }
              : msg
          )
        );
      }

      // Detect if user wants to REPLACE media or ADD to existing
      const replaceKeywords = /\b(replace|swap|change|instead of|use this|use these|switch to|new media|new images|new photos|new videos|different)\b/i;
      const shouldReplace = allNewMedia.length > 0 && replaceKeywords.test(text);

      // Collect media based on intent
      let allConversationMedia;
      if (shouldReplace) {
        // REPLACE: Use only newly added media (uploads + library selections)
        allConversationMedia = [...allNewMedia];
      } else {
        // ADD: Combine existing + new media
        allConversationMedia = [...conversationMedia];

        // Add newly added media (avoid duplicates)
        if (allNewMedia.length > 0) {
          allNewMedia.forEach(m => {
            if (!allConversationMedia.find(cm => cm.id === m.id)) {
              allConversationMedia.push(m);
            }
          });
        }
      }

      // Build media context message to inform LLM
      let mediaContext = '';
      if (allConversationMedia.length > 0) {
        if (shouldReplace && conversationMedia.length > 0) {
          mediaContext = `\n\n[User REPLACED previous media with: ${allConversationMedia.map(m => m.filename).join(', ')}. Use ONLY these new files in the campaign.]`;
        } else if (mediaUrls.length > 0 && conversationMedia.length > 0) {
          mediaContext = `\n\n[User ADDED new media. All available media: ${allConversationMedia.map(m => m.filename).join(', ')}. You can use any of these files.]`;
        } else {
          mediaContext = `\n\n[User uploaded media: ${allConversationMedia.map(m => m.filename).join(', ')}]`;
        }
      }

      // Ask user how to handle uploaded images if they haven't stated their intent.
      // Only prompt when NEW images are being added in this message — not for historical uploads.
      // NOTE: avoid matching generic campaign-creation words like "create" or "make" that
      // are unrelated to image handling — only match phrases that specifically address
      // what to do with images (variations, as-is, ai generation, etc.).
      let imageIntent: ImageIntent | undefined;
      const hasNewImages = allNewMedia.some(m => m.type === 'image');
      const hasExplicitImageIntent = /\b(new image|variation|ai image|use.*image|keep.*image|as.?is|generate.*image|image.*generat)\b/i.test(text);
      if (hasNewImages && !hasExplicitImageIntent) {
        imageIntent = await new Promise<ImageIntent>((resolve) => {
          setMediaIntentFilenames(allConversationMedia.filter(m => m.type === 'image').map(m => m.filename));
          setMediaIntentResolve(() => resolve);
        });
        setMediaIntentResolve(null);
        setMediaIntentFilenames([]);

        // Append the user's intent to the LLM context so it knows what to do
        if (imageIntent === 'use_uploaded') {
          mediaContext += '\n[User wants to use their uploaded images as the campaign creatives exactly as-is. Skip the image generation path entirely — do NOT emit media_generation_proposals and do NOT include imagePrompt on concepts. Look at the uploaded images and build the creative strategy (concepts, rationale, copy) around what you see in them. The backend will attach the uploaded images to each concept automatically.]';
        } else if (imageIntent === 'generate_variations') {
          mediaContext += '\n[User wants AI variations of their uploaded images. In your Flow A proposals you MUST use generation_mode: "image_to_image" and set source_asset_id to the UUID of the uploaded image.]';
        } else if (imageIntent === 'generate_new') {
          mediaContext += '\n[User wants completely new AI-generated images — do not base them on the uploaded images.]';
        }
      }

      // Check if streaming is enabled via environment variable
      const streamingEnabled = import.meta.env.VITE_STREAMING_ENABLED !== 'false';

      if (streamingEnabled) {
        // Use streaming endpoint
        await handleStreamingMessage(conversationId, text, allConversationMedia, mediaContext, selectedTools, tempId, imageIntent);
      } else {
        // Use non-streaming endpoint
        const response = await apiClient.post(
          `/api/v1/campaigns/${conversationId || 'new'}/prompt`,
          {
            message: text + mediaContext,
            media: allConversationMedia.map(m => ({
              id: m.id,
              type: m.type,
              filename: m.filename,       // Send original name to LLM
              storage_key: m.storage_key,  // Keep UUID for reference
              url: m.url
            })),
          }
        );

        if (response.data.error === 'insufficient_credits' || response.data.error === 'turn_limit') {
          setMessages(prev => [...prev.filter(msg => msg.id !== tempId), {
            id: `error-${Date.now()}`,
            role: 'system',
            content: {
              insufficient_credits: response.data.error === 'insufficient_credits',
              text: response.data.response,
            },
            created_at: new Date().toISOString(),
          }]);
          return;
        }

        // Phase 4: Extract structured fields from response (Instructor-based)
        const campaignName = response.data.campaign_name;
        if (campaignName && conversationId) {
          // Update conversation title via API
          try {
            await apiClient.patch(`/api/v1/campaigns/${conversationId}`, {
              title: campaignName,
            });
          } catch (err) {
            console.error('Failed to update campaign name:', err);
          }
        }

        // Display text is clean (no markers) - backend provides clean output
        // MEDIA tags are still embedded in text and will be parsed by RichMessageText
        const displayText = (response.data.response || '').trim();

        // Add assistant response with structured fields
        const assistantMessage: Message = {
          id: response.data.message_id || `msg-${Date.now()}`,
          role: 'assistant',
          content: {
            text: displayText,
            tool_results: response.data.tool_results,
            campaign_proposal: response.data.campaign_proposal,
            // Phase 4: Structured fields from Instructor-based LLM
            campaign_name: response.data.campaign_name,
            workflow_status: response.data.workflow_status,
            response_type: response.data.response_type,
            // Campaign strategy confirmation
            campaign_strategy: response.data.campaign_strategy,
          },
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, assistantMessage]);

        // Handle campaign strategy confirmation (non-streaming path)
        if (response.data.campaign_strategy && response.data.response_type === 'campaign_strategy_confirmation') {
          setActiveStrategy({
            strategy: response.data.campaign_strategy,
            campaignName: response.data.campaign_name
          });
        }

        // Phase 5: Detect media generation tool calls and open canvas
        if (response.data.tool_results) {
          detectMediaGeneration(response.data.tool_results);
        }
      }

      // Refresh sidebar title if campaign name was updated
      if (onConversationCreated) {
        onConversationCreated();
      }
    } catch (err: any) {
      console.error('Failed to send message:', err);
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      const isInsufficientCredits =
        (err.message && err.message.toLowerCase().includes('insufficient credits')) ||
        (err.response?.data?.error === 'insufficient_credits') ||
        (err.response?.data?.response && err.response.data.response.toLowerCase().includes('insufficient credits'));
      if (isInsufficientCredits) {
        const creditsErrorMessage: Message = {
          id: `error-${Date.now()}`,
          role: 'system',
          content: {
            insufficient_credits: true,
            text: "You've run out of credits. Upgrade your plan or purchase more credits to continue generating campaigns.",
          },
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, creditsErrorMessage]);
      } else {
        setError(err.message || 'Failed to send message');
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          role: 'system',
          content: {
            text: `Error: ${err.message || 'Failed to send message. Please try again.'}`,
          },
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // onPublish callback — status is handled by the 'campaignPublished' event listener above.
  const handlePublish = () => {};

  /**
   * Approve a stage gate from the canvas — the ONLY way stage advances.
   * The backend appends the approval to history; mirror it locally so the
   * transcript matches without a full reload.
   *
   * The launch gate is special: the confirm button IS the publish button.
   * The publishService wrapper routes to publish-multi / publish-organic /
   * scheduler (the same endpoints the old modals called), and the approval
   * is only recorded — stage only advances — if publishing succeeds.
   */
  /** Returns the failure message when a launch did not go through (the Launch panel shows it). */
  const handleApproveGate = async (gate: Gate, launchConfig?: Record<string, unknown>): Promise<string | void> => {
    try {
      if (gate === 'launch') {
        if (!conversationId || !user) return;
        const timing =
          (launchConfig?.timing as { type: 'now' | 'scheduled'; at?: string | null }) ??
          { type: 'now' };
        const mode = (launchConfig?.mode as 'paid' | 'organic') ?? 'paid';

        // Snapshot existing assignment IDs so the status card only shows
        // what this specific publish event created, not accumulated history.
        let existingAssignmentIds = new Set<string>();
        try {
          const prevResp = await apiClient.get(
            `/api/v1/conversations/${conversationId}/platform-assignments`,
          );
          existingAssignmentIds = new Set(
            (prevResp.data?.assignments ?? []).map((a: any) => a.id),
          );
        } catch {}

        const outcome = await publishCampaign({
          conversationId,
          userId: user.id,
          mode,
          timing,
          accounts: (launchConfig?.accounts as PublishAccount[]) ?? [],
          organicSelection: (launchConfig?.organicSelection as string[]) ?? [],
          campaignState: canvas.state,
          // Same list the canvas previews use as their image fallback — without it a
          // concept whose only image is an uploaded one publishes with no image at all.
          uploadedImages: conversationMedia.filter((m) => m.type === 'image'),
          isDemo,
          budgetHealthSnapshot: launchConfig?.budgetHealthSnapshot as Record<string, { status: string; implications: string }> | undefined,
        });
        if (!outcome.ok) {
          const failed = outcome.error || 'Publishing failed — the campaign was not launched.';
          setError(failed);
          return failed; // no approval, stage stays at launch
        }

        // Keep the conversation status in sync (same PATCH the old flow used)
        if (!outcome.scheduled && outcome.status) {
          apiClient
            .patch(`/api/v1/conversations/${conversationId}/status`, { status: outcome.status })
            .catch(() => {});
        }

        // Gate approval advances stage: launch → live. Skip when already live
        // (re-publish from live panel) — gate is already sealed and cannot be
        // re-approved; the publish above is all that's needed.
        if (canvas.stage !== 'live') {
          await canvas.approveGate('launch', launchConfig);
        } else if (mode === 'organic' && !outcome.scheduled) {
          // Re-publishing organically from live stage: persist the newly posted
          // concept IDs into canvas state so ✓ Posted badges survive a refresh.
          const prevSelection = (canvas.state.launch?.organicSelection ?? []) as string[];
          const newSelection = (launchConfig?.organicSelection as string[]) ?? [];
          const merged = [...new Set([...prevSelection, ...newSelection])];
          canvas.submitEdit('launch.organicSelection', merged, 'Updated posted concepts').catch(() => {});
        }

        // Surface only the assignments created by this publish event.
        try {
          const assignmentsResp = await apiClient.get(
            `/api/v1/conversations/${conversationId}/platform-assignments`,
          );
          const raw = assignmentsResp.data?.assignments;
          const allAssignments = Array.isArray(raw) ? raw : [];
          // Filter to assignments that didn't exist before this publish.
          const freshAssignments = allAssignments.filter(
            (a: any) => !existingAssignmentIds.has(a.id),
          );
          const assignments = freshAssignments.length > 0 ? freshAssignments : allAssignments;
          const statusContent = outcome.scheduled
            ? {
                published_campaign_results: {
                  assignments: ((launchConfig?.accounts as any[]) ?? [])
                    .filter((a: any) => a.enabled)
                    .map((a: any) => ({
                      id: a.connectionId,
                      platform: a.platform,
                      platform_account_id: a.accountId,
                      account_name: a.accountName,
                      platform_campaign_id: null,
                      status: 'scheduled',
                      platform_campaign_status: null,
                      platform_campaign_status_synced_at: null,
                      published_at: null,
                      publish_type: mode === 'paid' ? 'paid_ad' : 'organic_post',
                      organic_post_ids: null,
                      organic_post_urls: null,
                    })),
                },
              }
            : { published_campaign_results: { assignments } };
          setMessages(prev => [
            ...prev,
            {
              id: `publish-status-${Date.now()}`,
              role: 'assistant' as const,
              content: statusContent,
              created_at: new Date().toISOString(),
            },
          ]);
          await apiClient.post(`/api/v1/conversations/${conversationId}/messages`, {
            role: 'assistant',
            content: statusContent,
          });
        } catch {
          // Status card is cosmetic — the publish itself already succeeded.
        }
        return;
      }

      await canvas.approveGate(gate, launchConfig);
      const progressLabel =
        gate === 'strategy' ? 'Generating creative concepts...' : 'Setting up launch...';
      setMessages(prev => [
        ...prev,
        {
          id: `stage-progress-${Date.now()}`,
          role: 'system' as const,
          content: { text: progressLabel, type: 'stage_progress' },
          created_at: new Date().toISOString(),
        },
      ]);
      // Let the assistant react to the approval — LLM sees the instruction but
      // no user bubble is added to the transcript.
      await handleSendMessage(
        gate === 'strategy'
          ? 'Strategy approved — generate the creative concepts.'
          : 'Creative set approved — set up the launch.',
        undefined, undefined, undefined, undefined, true
      );
    } catch (e: any) {
      // canvas.approveGate re-throws after setting canvas.error; for any other
      // error (e.g. publishCampaign's outer GET failing), surface it here.
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d: any) => d.msg ?? JSON.stringify(d)).join('; ')
        : typeof detail === 'string' ? detail : e?.message || 'Something went wrong — please try again.';
      setError(msg);
      return msg;
    }
  };

  /** Suggestion chip clicked — send it as a normal chat message. */
  const handlePickSuggestion = (text: string) => {
    canvas.setSuggestions([]);
    handleSendMessage(text);
  };

  const conversationMedia = React.useMemo(() => {
    const mediaList: Array<{
      id: string;
      filename: string;      // Original name for LLM and display
      storageKey: string;    // UUID for storage reference
      url: string;
      type: string;
    }> = [];

    // Phase 3.5: Merge media from messages AND fetched conversation media (for reload scenario)
    const seenFilenames = new Set<string>();

    // Add media from messages (during active session)
    messages.forEach(msg => {
      if (msg.content.media) {
        console.log('[conversationMedia] Found media in message:', {
          msgId: msg.id,
          role: msg.role,
          mediaCount: msg.content.media.length,
          media: msg.content.media.map(m => ({ filename: m.filename, url: m.url }))
        });
        msg.content.media.forEach(m => {
          if (m.filename && !seenFilenames.has(m.filename)) {
            mediaList.push({
              id: m.id || m.filename,         // Use id if available
              filename: m.filename,            // Now original name from backend
              storageKey: m.storage_key || m.filename,  // UUID for storage
              url: m.url,
              type: m.type
            });
            seenFilenames.add(m.filename);
          }
        });
      }
    });

    // Add fetched conversation media (from reload scenario)
    fetchedConversationMedia.forEach(m => {
      if (m.filename && !seenFilenames.has(m.filename)) {
        mediaList.push({
          id: m.id,
          filename: m.filename,
          storageKey: m.storage_key,
          url: m.url,
          type: m.type
        });
        seenFilenames.add(m.filename);
      }
    });

    console.log('[conversationMedia] Final media list:', {
      totalMessages: messages.length,
      fetchedMediaCount: fetchedConversationMedia.length,
      finalMediaCount: mediaList.length,
      mediaList: mediaList.map(m => ({ filename: m.filename, url: m.url }))
    });

    return mediaList;
  }, [messages, fetchedConversationMedia]);

  // Original (non-AI) media for the canvas selection panel.
  // Excludes ai_generated assets to avoid duplication with the generated media section.
  const originalMediaForCanvas = React.useMemo(() => {
    return conversationMedia
      .filter(m => {
        const fetched = fetchedConversationMedia.find(f => f.id === m.id);
        if (fetched) return fetched.source !== 'ai_generated';
        return true; // message-embedded media (active session) is always user-uploaded
      })
      .map(m => ({
        id: m.id,
        url: m.url,
        filename: m.filename,
        media_type: (m.type === 'video' ? 'video' : 'image') as 'image' | 'video',
      }));
  }, [conversationMedia, fetchedConversationMedia]);

  if (loading && messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-ink-2">Loading conversation...</p>
        </div>
      </div>
    );
  }

  const canvasSplit = canvas.canvasActive;

  return (
    <div className="flex flex-col h-full bg-background-light dark:bg-background-dark relative">
      {/* Phone (phone campaign board): back, name, step, and the Chat / Campaign switch */}
      {isMobile && conversationId && (
        <div className="flex flex-col gap-3 border-b border-line bg-ground px-4 pb-3 pt-3">
          <div className="flex items-center gap-3">
            <Link to="/campaigns" aria-label="Back to campaigns" className="grid h-11 w-11 shrink-0 place-items-center rounded-btn bg-inset text-ink">
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <div className="min-w-0">
              <div className="truncate font-display text-[18px] font-extrabold tracking-[-0.02em] text-ink">{(canvas.state?.name as string) || 'New campaign'}</div>
              {canvas.stage && (() => {
                const order = ['brief', 'strategy', 'creative', 'launch', 'live'];
                const i = order.indexOf(canvas.stage as string);
                const label = (canvas.stage as string).charAt(0).toUpperCase() + (canvas.stage as string).slice(1);
                return <div className="text-xs font-bold text-brand-ink">{label}{i >= 0 && ` · step ${i + 1} of ${order.length}`}</div>;
              })()}
            </div>
          </div>
          {canvasSplit && (
            <div role="tablist" aria-label="View" className="grid grid-cols-2 gap-1 rounded-btn bg-inset p-1">
              {(['chat', 'canvas'] as const).map((tab) => (
                <button key={tab} type="button" role="tab" aria-selected={mobileTab === tab} onClick={() => setMobileTab(tab)}
                  className={cn('min-h-[40px] rounded-[9px] font-display text-sm font-bold transition-colors',
                    mobileTab === tab ? 'bg-card text-ink shadow-ring-line' : 'text-ink-2')}>
                  {tab === 'chat' ? 'Chat' : 'Campaign'}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Chat pane (full width until the canvas activates) */}
        <div
          className={cn(
            'flex min-h-0 flex-col relative',
            canvasSplit
              ? isMobile
                ? mobileTab === 'chat'
                  ? 'flex-1'
                  : 'hidden'
                // Campaign page board: the chat is the "Campaign assistant" side panel.
                : chatOpen ? 'order-2 w-[400px] shrink-0 border-l border-line bg-card' : 'hidden'
              : 'flex-1',
          )}
        >
      {canvasSplit && !isMobile && (
        <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <span className="flex items-center gap-2.5 font-display text-base font-bold text-ink">
            <Logo mark to={null} height={28} />
            Campaign assistant
          </span>
          <button type="button" onClick={() => setChatOpen(false)} aria-label="Close the assistant"
            className="grid h-9 w-9 place-items-center rounded-lg text-ink-2 hover:bg-ground hover:text-ink">×</button>
        </div>
      )}
      {/* Demo campaign banner */}
      {isDemo && (
        <div className="px-4 py-2 border-b border-primary/20 bg-primary/5 flex items-center gap-2">
          <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-primary/20 text-primary border border-primary/30 uppercase flex-shrink-0">
            Demo
          </span>
          <p className="text-xs text-ink-2 flex-1">
            This is a demo campaign — AI responses are scripted and publishing creates no real ads.
          </p>
          <button
            onClick={handleDemoReset}
            disabled={demoResetting}
            className="flex-shrink-0 text-[11px] text-ink-3 hover:text-primary border border-border hover:border-primary/40 rounded px-2 py-0.5 transition-colors disabled:opacity-50"
          >
            {demoResetting ? 'Resetting…' : '↺ Restart demo'}
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="px-4 md:px-6 py-3 border-b border-border-light dark:border-border-dark">
          <div className="max-w-4xl mx-auto">
            <Alert variant="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-3 md:p-6 lg:p-8 overscroll-contain"
      >
        <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
          {!canvasSplit && !loading && messages.every(m => m.role !== 'user') && messages.length <= 1 ? (
            <div className="flex flex-1 flex-col justify-center overflow-y-auto px-5 py-8 md:px-12">
              <div className="mx-auto flex w-full max-w-[640px] flex-col gap-5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-label uppercase text-ink-3">Brand new campaign</span>
                  <span className="inline-flex h-6 items-center rounded-full bg-inset px-2.5 text-xs font-semibold text-ink-2">Draft</span>
                </div>
                <div className="flex gap-1.5" aria-hidden="true">{[0, 1, 2, 3, 4].map(i => <span key={i} className="h-1.5 flex-1 rounded-full bg-line" />)}</div>
                <h1 className="m-0 font-display text-[34px] font-extrabold leading-[1.05] tracking-[-0.035em] text-ink md:text-[44px]">What do you want to promote?</h1>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Get more sign-ups', text: 'I want more sign-ups for ' },
                    { label: 'Festive offer', text: 'A festive offer: ' },
                    { label: 'Launch a product', text: 'We’re launching ' },
                  ].map(c => (
                    <button key={c.label} type="button" onClick={() => setDraft(d => ({ text: c.text, n: d.n + 1 }))}
                      className="min-h-[36px] rounded-full bg-brand-soft px-3.5 text-[13px] font-bold text-brand-ink hover:bg-brand/20">
                      {c.label}
                    </button>
                  ))}
                  <button type="button"
                    onClick={async () => { try { const r = await apiClient.post('/api/v1/conversations/ensure-demo'); if (r.data?.id) navigate(`/campaigns/${r.data.id}`); } catch { navigate('/campaigns'); } }}
                    className="min-h-[36px] rounded-full bg-inset px-3.5 text-[13px] font-bold text-navy-ink hover:bg-line">
                    Try a demo campaign
                  </button>
                </div>
                <p className="m-0 text-small text-ink-2">
                  Describe it below, or drop a photo. Without a photo, Admeru makes lifestyle and product images from your description, with your logo when it’s in your brand. For text-led designs, use <Link to="/ad-studio" className="font-semibold text-brand-ink">Ad Studio</Link> and add them to the campaign.
                </p>
              </div>
            </div>
          ) : (
          <MessageList
            messages={messages}
            conversationMedia={conversationMedia}
            conversationId={conversationId}
            onPublish={handlePublish}
            onToggleMediaCanvas={toggleCanvas}
            isMediaCanvasOpen={isMediaCanvasOpen}
          />
          )}

          {/* Campaign Strategy Confirmation Card */}
          {activeStrategy && (
            <CampaignStrategyConfirmationCard
              strategy={activeStrategy.strategy}
              campaignName={activeStrategy.campaignName}
              onApprove={handleStrategyApprove}
              onRequestChanges={handleStrategyRequestChanges}
              orgWebsiteUrl={currentOrganization?.website_url}
              orgPhone={currentOrganization?.phone}
              orgIndustry={currentOrganization?.industry}
              orgTargetCountries={currentOrganization?.target_countries}
            />
          )}

          {/* Image Intent Card — shown while waiting for user to choose how to handle uploaded images */}
          {mediaIntentResolve && (
            <MediaIntentCard
              filenames={mediaIntentFilenames}
              onIntent={(intent) => mediaIntentResolve(intent)}
            />
          )}

          {/* Media Generation Proposals Card */}
          {activeProposals && activeProposals.length > 0 && (
            <MediaProposalCard
              proposals={activeProposals}
              onApprove={handleProposalApprove}
              onSkip={handleProposalSkip}
              loading={proposalLoading}
              maxGenerations={maxGenerations}
            />
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Scroll to Bottom Button */}
      <ScrollToBottomButton
        containerRef={messagesContainerRef}
        threshold={isMobile ? 150 : 200}
      />

      {/* Suggestion chips from the assistant's last envelope */}
      {canvas.suggestions.length > 0 && !loading && canvas.stage !== 'live' && (
        <div className="border-t border-border px-3 py-2 md:px-4">
          <SuggestionChips
            suggestions={canvas.suggestions}
            onPick={handlePickSuggestion}
            disabled={loading}
          />
        </div>
      )}

      {/* Input Area — locked once campaign is live */}
      {canvas.stage === 'live' ? (
        <div className="flex items-center gap-2.5 border-t border-border px-4 py-3 text-xs text-ink-3">
          <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Campaign is live — start a new conversation to create another campaign
        </div>
      ) : (
        <>
          <PlanBriefBanner conversationId={conversationId} hasMessages={messages.length > 0} loading={loading}
            onSend={(text, displayText) => handleSendMessage(text, undefined, undefined, undefined, displayText)} />
          {!messages.some(m => m.role === 'user') && <SetupFirstBanner where="campaign" className="mx-4 mb-1" />}
          <InputArea
            draft={draft}
            onSend={handleSendMessage}
            loading={loading}
            conversationId={conversationId}
          />
        </>
      )}
        </div>

        {/* Campaign canvas (right pane) */}
        {canvasSplit && (
          <CampaignCanvas
            canvas={canvas}
            onApprove={handleApproveGate}
            conversationId={conversationId}
            loading={loading}
            isDemo={isDemo}
            uploadedImages={conversationMedia.filter(m => m.type === 'image')}
            className={cn('order-1 min-w-0 flex-1', isMobile && mobileTab !== 'canvas' && 'hidden')}
            chatOpen={isMobile ? undefined : chatOpen}
            onToggleChat={isMobile ? undefined : () => setChatOpen(o => !o)}
            onAskChat={(text) => { if (isMobile) setMobileTab('chat'); else setChatOpen(true); handleSendMessage(text); }}
          />
        )}
      </div>


      {/* Media Workflow Status Bar - Shows when workflow is active */}
      {conversationId && workflowState && workflowState.state !== 'none' && workflowState.state !== 'completed' && (
        <MediaWorkflowStatusBar
          state={workflowState.state}
          jobCount={workflowState.job_ids?.length || 0}
          completedCount={workflowState.all_jobs_complete ? (workflowState.job_ids?.length || 0) : 0}
          onOpenCanvas={toggleCanvas}
          isCanvasOpen={isMediaCanvasOpen}
        />
      )}

      {/* Media Generation Canvas (Two-Phase Workflow) */}
      {conversationId && (
        <MediaGenerationCanvas
          isOpen={isMediaCanvasOpen}
          onClose={handleMediaCanvasClose}
          conversationId={conversationId}
          onContinue={handleMediaContinue}
          onError={handleMediaError}
          originalMedia={originalMediaForCanvas}
        />
      )}

      {/* Right-side floating contextual panel — replaced by the campaign
          canvas once the conversation has canvas state */}
      {!canvasSplit && <ChatSidePanel messages={messages} />}
    </div>
  );
};

export default ChatInterface;
